import {
  Asset,
  ClockIcon,
  Pill,
  ReplyIcon,
  WarningIcon,
} from '@clearcover/atlas';
import { GetPolicyDraftResponseType } from '@clearcover/core-app-types';
import React, { FC } from 'react';
import { PageHeaderBar } from 'Shared/components';
import { formatDateShortWithAt } from 'Shared/utils';
import styles from './styles.module.css';

function getReturnLink({
  draftType,
  policyId,
  renewalDraftId,
}: {
  policyId: string;
  renewalDraftId: string | undefined;
  draftType: GetPolicyDraftResponseType['policyDraft']['draftType'] | undefined;
}) {
  switch (draftType) {
    case 'renewal_quote':
      return {
        text: 'Exit draft',
        href: `/policies/${policyId}/draft/${renewalDraftId}`,
      };
    case 'renewal':
      return {
        text: 'Return to active term',
        href: `/policies/${policyId}`,
      };
    default:
      return {
        text: 'Exit draft',
        href: `/policies/${policyId}`,
      };
  }
}

function getHeaderContents(
  draftType: GetPolicyDraftResponseType['policyDraft']['draftType'] | undefined
) {
  switch (draftType) {
    case 'renewal':
      return {
        pillContents: 'Renewal Term',
        headerText: 'You are viewing a future renewal term.',
      };
    case 'renewal_quote':
      return {
        pillContents: 'Renewal Term Draft',
        headerText: 'You are viewing a renewal term draft.',
      };
    default:
      return {
        pillContents: 'Draft',
        headerText: 'You are viewing a saved draft.',
      };
  }
}

export const PolicyDraftHeaderBar: FC<{
  policyId: string;
  renewalDraftId: string | undefined;
  draftType: GetPolicyDraftResponseType['policyDraft']['draftType'] | undefined;
  updatedAt: string | undefined;
  timeZone: string;
}> = ({ policyId, renewalDraftId, draftType, updatedAt, timeZone }) => {
  const returnLink = getReturnLink({ draftType, policyId, renewalDraftId });
  const { pillContents, headerText } = getHeaderContents(draftType);

  return (
    <SharedHeaderBar
      pillContents={pillContents}
      headerText={headerText}
      returnLink={returnLink}
      draftType={draftType}
      updatedAt={updatedAt}
      timeZone={timeZone}
    />
  );
};

export const PolicyDraftSummaryHeaderBar: FC<{
  policyId: string;
  renewalDraftId: string | undefined;
  draftType: GetPolicyDraftResponseType['policyDraft']['draftType'] | undefined;
  updatedAt: string | undefined;
  timeZone: string;
}> = ({ policyId, renewalDraftId, draftType, updatedAt, timeZone }) => {
  const returnLink = getReturnLink({ draftType, policyId, renewalDraftId });
  const { pillContents, headerText } =
    draftType === 'mid_term' && renewalDraftId
      ? {
          pillContents: (
            <Asset
              icon={WarningIcon}
              description="warning"
              height={16}
              width={16}
            />
          ),
          headerText:
            'By applying these changes, the upcoming renewal will be updated as well.',
        }
      : getHeaderContents(draftType);
  const className =
    draftType === 'mid_term' && renewalDraftId ? styles.renewalWarning : '';

  return (
    <SharedHeaderBar
      pillContents={pillContents}
      headerText={headerText}
      returnLink={returnLink}
      draftType={draftType}
      updatedAt={updatedAt}
      timeZone={timeZone}
      className={className}
    />
  );
};

const SharedHeaderBar: FC<{
  pillContents: string | React.JSX.Element;
  headerText: string;
  returnLink: { text: string; href: string };
  draftType: GetPolicyDraftResponseType['policyDraft']['draftType'] | undefined;
  updatedAt: string | undefined;
  timeZone: string;
  className?: string;
}> = ({
  pillContents,
  headerText,
  returnLink,
  draftType,
  updatedAt,
  timeZone,
  className,
}) => {
  return (
    <PageHeaderBar
      className={`${className} ${styles.policyDraftHeaderBar} ${
        draftType === 'renewal' ? styles.futureRenewal : ''
      }`.trim()}
    >
      <Pill className={styles.pill} variant="draft">
        {pillContents}
      </Pill>
      <span className={styles.draftType}>{headerText}</span>
      <span className={styles.dateAndLink}>
        {updatedAt && (
          <span className={styles.dateAndLinkItem}>
            <Asset
              icon={ClockIcon}
              description="Driver Change Summary"
              height={16}
              width={16}
              className={styles.icon}
            />
            <span className={styles.savedOn}>
              Last Updated {formatDateShortWithAt(updatedAt, timeZone)}
            </span>
          </span>
        )}
        <a
          data-testid={`return-link`}
          href={returnLink.href}
          className={styles.dateAndLinkItem}
        >
          <Asset
            icon={ReplyIcon}
            description="Driver Change Summary"
            height={16}
            width={16}
            className={styles.icon}
          />
          <span>{returnLink.text}</span>
        </a>
      </span>
    </PageHeaderBar>
  );
};
